import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ChevronRight, ChevronLeft, Users, Plus, Check, Download, X, Printer, Bell } from "lucide-react";
import QRCode from "qrcode";
import { useAuth } from "@/auth";
import { apiFetch, downloadFile } from "@/api";
import AppleSignIn from "@/components/AppleSignIn";
import { Button, Field, Spinner, cn } from "@/ui";
import { LEARNING_LEVELS } from "@/constants";
import { QRImage } from "@/components/QRImage";
import { AffiliateCenter } from "./AffiliateAdmin";
import { TrialCenter } from "./TrialAdmin";
import { SchoolCenter, SchoolAdminPanel, TeacherPanel } from "./SchoolAdmin";
import { fmtDate, fmtMonth } from "@/format";
import { AffiliateRangeFilter } from "@/components/AffiliateRangeFilter";

export default function Portal() {
  const { account, role } = useAuth();
  const loc = useLocation();
  const sub = loc.pathname.split("/")[2];
  const ROLE_FOR_SUB = { parent: "parent", affiliate: "affiliate", developer: "developer", school: "school_admin", teacher: "teacher" };
  if (sub && account && role === ROLE_FOR_SUB[sub]) {
    if (role === "parent") return <ParentDash />;
    if (role === "affiliate") return <AffiliateDash />;
    if (role === "developer") return <DeveloperDash />;
    if (role === "school_admin") return <SchoolAdminDash />;
    if (role === "teacher") return <TeacherDash />;
  }
  return <Login initialRole={sub || "parent"} />;
}

function SchoolAdminDash() {
  const signOut = useSignOut();
  return <Shell title="School admin" onSignOut={signOut}><SchoolAdminPanel /></Shell>;
}

function TeacherDash() {
  const signOut = useSignOut();
  return <Shell title="Teacher" onSignOut={signOut}><TeacherPanel /></Shell>;
}

function Shell({ title, children, onSignOut }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background/80 px-5 py-3 backdrop-blur-xl">
        <h1 className="text-lg font-semibold">{title}</h1>
        {onSignOut ? <button data-testid="signout" onClick={onSignOut}><LogOut className="h-5 w-5" /></button> : null}
      </header>
      <div className="mx-auto max-w-4xl px-5 py-6">{children}</div>
    </div>
  );
}

function Login({ initialRole }) {
  const navigate = useNavigate();
  const { portalLogin, developerLogin, parentRegister, appleSignIn } = useAuth();
  const roleKey = ["parent", "affiliate", "developer", "school", "teacher"].includes(initialRole) ? initialRole : "parent";
  const isParent = roleKey === "parent";
  const [register, setRegister] = useState(isParent); // parents default to creating an account
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfa, setMfa] = useState(null); // { setup?, uri?, secret?, required? }
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const TITLES = { parent: "Parent account", affiliate: "Affiliate portal", developer: "Developer", school: "School admin", teacher: "Teacher" };
  const COPY = {
    parent: "Create your parent account to add and manage your child's access.",
    affiliate: "Sign in to your affiliate area to track referrals and earnings.",
    developer: "Sign in to the Developer area. This is separate from the MAXx app experience.",
    school: "Sign in to manage your school's teachers, seats and licence.",
    teacher: "Sign in to see your class, seats and enrollment link.",
  };

  const submit = async () => {
    setErr("");
    if (!email.includes("@") || !password) return setErr("Enter your email and password.");
    setLoading(true);
    try {
      if (isParent && register) { await parentRegister(email.trim(), password); navigate("/portal/parent"); return; }
      if (roleKey === "developer") {
        const data = await developerLogin(email.trim(), password, code.trim() || undefined);
        if (data.token) { navigate("/portal/developer"); return; }
        if (data.mfa_setup) { setMfa({ setup: true, uri: data.otpauth_uri, secret: data.secret }); if (data.error) setErr(data.error); return; }
        if (data.mfa_required) { setMfa({ required: true }); return; }
        return;
      }
      await portalLogin(roleKey, email.trim(), password);
      navigate(`/portal/${roleKey}`);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  const onAppleParent = async (cred) => {
    setErr("");
    try {
      await appleSignIn("parent", cred);
      navigate("/portal/parent");
    } catch (e) {
      setErr(e.message);
    }
  };

  if (mfa) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
        <div className="w-full max-w-md flex flex-col gap-5">
          <button className="flex items-center gap-1 text-sm text-muted-foreground" onClick={() => { setMfa(null); setCode(""); setErr(""); }}><ChevronLeft className="h-4 w-4" /> Back</button>
          <h1 className="text-3xl font-semibold tracking-tight">Two-factor verification</h1>
          {mfa.setup ? (
            <>
              <p className="text-sm text-muted-foreground">
                Add this account to an authenticator app (Google Authenticator, Authy, 1Password), then enter the current 6-digit code to finish setup.
              </p>
              <div className="flex flex-col items-center gap-3 rounded-2xl border border-border p-4">
                <QRImage value={mfa.uri} size={160} />
                <p className="text-xs text-muted-foreground break-all text-center" data-testid="mfa-secret">Or enter this key manually: <span className="font-mono">{mfa.secret}</span></p>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Enter the current 6-digit code from your authenticator app.</p>
          )}
          <Field label="6-digit code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" inputMode="numeric" data-testid="mfa-code" />
          {err ? <p data-testid="mfa-error" className="text-sm font-semibold">{err}</p> : null}
          <Button data-testid="mfa-submit" onClick={submit} loading={loading}>{mfa.setup ? "Verify & finish setup" : "Verify"}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <div className="w-full max-w-md flex flex-col gap-5">
        <button className="flex items-center gap-1 text-sm text-muted-foreground" onClick={() => navigate("/onboarding")}><ChevronLeft className="h-4 w-4" /> Back</button>
        <img src="/maxx-logo-onlight.png" alt="MAXx" className="h-11 w-auto self-start dark:hidden" />
        <img src="/maxx-logo-ondark.png" alt="MAXx" className="hidden h-11 w-auto self-start dark:block" />
        <h1 className="text-3xl font-semibold tracking-tight">{TITLES[roleKey]}</h1>
        <p className="text-sm text-muted-foreground">{COPY[roleKey]}</p>
        <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@email.com" data-testid="portal-email" />
        <Field label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" data-testid="portal-password" />
        {err ? <p data-testid="portal-error" className="text-sm font-semibold">{err}</p> : null}
        <Button data-testid="portal-submit" onClick={submit} loading={loading}>{isParent && register ? "Create parent account" : "Sign in"}</Button>
        {isParent ? (
          <button data-testid="parent-register-toggle" className="text-center text-sm text-muted-foreground" onClick={() => setRegister((r) => !r)}>
            {register ? "Already have a parent account? Sign in" : "Create a parent account"}
          </button>
        ) : null}
        {roleKey === "school" ? (
          <button data-testid="teacher-login-switch" className="text-center text-sm text-muted-foreground" onClick={() => navigate("/portal/teacher")}>
            I'm a teacher →
          </button>
        ) : null}
        {roleKey === "teacher" ? (
          <button data-testid="school-login-switch" className="text-center text-sm text-muted-foreground" onClick={() => navigate("/portal/school")}>
            I'm a school admin →
          </button>
        ) : null}
        {isParent ? (
          <>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <div className="h-px flex-1 bg-border" /> or <div className="h-px flex-1 bg-border" />
            </div>
            <AppleSignIn onCredential={onAppleParent} onError={setErr} />
          </>
        ) : null}
      </div>
    </div>
  );
}

function useSignOut() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  return () => { signOut(); navigate("/portal"); };
}

function ParentDash() {
  const { account } = useAuth();
  const signOut = useSignOut();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState({ mode: "home" });
  const [addOpen, setAddOpen] = useState(false);
  const [child, setChild] = useState({ name: "", learning_level: "", email: "", password: "", coupon_code: "" });
  const [cards, setCards] = useState([]);
  const [cardErrors, setCardErrors] = useState([]);
  const [cardsOpen, setCardsOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", learning_level: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await apiFetch("/parent/me")); } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openActivity = async (u) => { try { const d = await apiFetch(`/parent/users/${u.id}/activity`); setView({ mode: "activity", user: u, items: d.chats }); } catch (e) { toast(e.message); } };
  const openChat = async (u, c) => { try { const d = await apiFetch(`/parent/users/${u.id}/chats/${c.id}/messages`); setView({ mode: "chat", user: u, chat: c, items: d.messages }); } catch (e) { toast(e.message); } };
  const manage = async (u) => { try { await apiFetch("/parent/subscription/manage", { method: "POST", body: { user_id: u.id } }); toast("Subscription activated (demo)."); load(); } catch (e) { toast(e.message); } };
  const payChild = async (u) => { try { const d = await apiFetch("/payments/parent/checkout", { method: "POST", body: { user_id: u.id, origin_url: window.location.origin } }); if (d.checkout_url) { window.location.href = d.checkout_url; return; } toast("Couldn't start checkout."); } catch (e) { toast(e.message); } };
  const APP_URL = window.location.origin;
  const accessUrl = (tok) => `${APP_URL}/login?qt=${encodeURIComponent(tok)}`;
  const linkUrl = (tok) => `${APP_URL}/parent-link?token=${encodeURIComponent(tok)}`;
  const cardText = (c) => `MAXx login for ${c.name}\nLog in at: ${APP_URL}\nEmail: ${c.email}${c.password ? `\nPassword: ${c.password}` : ""}\n\nOpen MAXx, tap Log in, then scan the Kid access QR to sign in — or enter the email and password above.`;
  const copyCard = async (c) => { try { await navigator.clipboard.writeText(cardText(c)); toast("Login copied."); } catch { toast("Copy not available."); } };
  const shareCard = async (c) => { const text = cardText(c); if (navigator.share) { try { await navigator.share({ title: `MAXx login for ${c.name}`, text }); return; } catch {} } copyCard(c); };
  const copyAllCards = async () => { try { await navigator.clipboard.writeText(cards.map(cardText).join("\n\n———\n\n")); toast("All logins copied."); } catch { toast("Copy not available."); } };
  const loginUrl = (c) => {
    if (c.quick_login_token) return accessUrl(c.quick_login_token);
    const code = c.code || c.login_code;
    if (code) return `${APP_URL}/login?code=${encodeURIComponent(code)}`;
    return `${APP_URL}/login?e=${encodeURIComponent(c.email)}&p=${encodeURIComponent(c.password)}`;
  };
  const esc = (s) => String(s || "").replace(/[&<>"]/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[m]));
  const printSheet = async () => {
    try {
      const withQr = await Promise.all(cards.map(async (c) => ({ ...c, qr: await QRCode.toDataURL(loginUrl(c), { margin: 1, width: 220 }) })));
      const body = withQr.map((c) => `<div class="card"><img src="${c.qr}"/><div><h2>${esc(c.name)} <span>· ${esc(c.learning_level)}</span></h2><div class="row">Email: <b>${esc(c.email)}</b></div>${c.password ? `<div class="row">Password: <b>${esc(c.password)}</b></div>` : ""}<div class="hint">Open MAXx, tap Log in, then scan this code${c.password ? " or type the email &amp; password" : ""}.</div></div></div>`).join("");
      const html = `<!doctype html><html><head><title>MAXx — child logins</title><meta charset="utf-8"/><style>body{font-family:system-ui,-apple-system,sans-serif;padding:28px;color:#111}h1{font-size:22px;margin:0 0 4px}p.sub{color:#666;margin:0 0 22px;font-size:13px}.card{display:flex;gap:18px;align-items:center;border:1px solid #ddd;border-radius:14px;padding:18px;margin-bottom:14px;page-break-inside:avoid}.card h2{margin:0 0 8px;font-size:17px}.card h2 span{font-weight:400;color:#666;font-size:13px}.row{font-size:14px;margin:3px 0}.row b{font-family:ui-monospace,monospace}.hint{font-size:12px;color:#666;margin-top:8px}img{width:120px;height:120px}</style></head><body><h1>MAXx — child logins</h1><p class="sub">Scan the QR with the MAXx app to sign in, or enter the email and password. Keep this sheet private.</p>${body}<script>window.onload=function(){setTimeout(function(){window.print()},250)}</script></body></html>`;
      const w = window.open("", "_blank");
      if (!w) { toast("Allow pop-ups to print the sheet."); return; }
      w.document.write(html); w.document.close();
    } catch (e) { toast("Couldn't build the print sheet."); }
  };
  const regenLinkQr = async () => { try { await apiFetch("/parent/link-qr/regenerate", { method: "POST" }); toast("New Link QR ready."); load(); } catch (e) { toast(e.message); } };
  const regenAccessQr = async (u) => { try { await apiFetch(`/parent/users/${u.id}/access-qr/regenerate`, { method: "POST" }); toast("New access QR ready."); load(); } catch (e) { toast(e.message); } };
  const openEdit = (u) => { setEditForm({ name: u.name || "", learning_level: u.learning_level || "" }); setEditUser(u); };
  const saveEdit = async () => {
    if (!editForm.name.trim()) return toast("Enter a name.");
    try {
      await apiFetch(`/parent/users/${editUser.id}`, { method: "PATCH", body: { name: editForm.name.trim(), learning_level: editForm.learning_level } });
      setEditUser(null);
      toast("Child updated.");
      load();
    } catch (e) { toast(e.message); }
  };
  const createChild = async (keepOpen) => {
    if (!child.name.trim()) return toast("Enter the child's name.");
    if (!child.learning_level) return toast("Choose a learning level.");
    if (!child.email.includes("@")) return toast("Enter a valid email.");
    if (child.password.length < 6) return toast("Password must be at least 6 characters.");
    const cred = { name: child.name.trim(), email: child.email.trim(), password: child.password, learning_level: child.learning_level };
    try {
      const resp = await apiFetch("/parent/users", { method: "POST", body: {
        name: cred.name, learning_level: cred.learning_level, email: cred.email,
        password: cred.password, coupon_code: child.coupon_code.trim() || undefined } });
      setCardErrors([]);
      setCards((cs) => [...cs, { ...cred, quick_login_token: resp?.user?.quick_login_token }]);
      setChild({ name: "", learning_level: "", email: "", password: "", coupon_code: "" });
      if (keepOpen) { toast(`${cred.name} added — add another.`); }
      else { setAddOpen(false); setCardsOpen(true); }
      load();
    } catch (e) { toast(e.message); }
  };
  const unlink = async (u) => { if (!window.confirm(`Remove the link to ${u.name}? Their account and chats stay; they just won't be connected to you anymore.`)) return; try { await apiFetch(`/parent/users/${u.id}/unlink`, { method: "POST" }); toast("Link removed."); setView({ mode: "home" }); load(); } catch (e) { toast(e.message); } };
  const remove = async (u) => { if (!window.confirm(`Delete ${u.name}'s account, chats and memory?`)) return; try { await apiFetch(`/parent/users/${u.id}`, { method: "DELETE" }); toast("Account deleted."); setView({ mode: "home" }); load(); } catch (e) { toast(e.message); } };

  if (loading && !data) return <Shell title="Parent" onSignOut={signOut}><div className="flex justify-center py-16"><Spinner className="h-6 w-6 text-foreground" /></div></Shell>;

  if (view.mode === "chat")
    return (
      <Shell title={view.chat?.title || "Chat"} onSignOut={signOut}>
        <button className="mb-4 flex items-center gap-1 text-sm" onClick={() => setView({ mode: "activity", user: view.user, items: view.items })}><ChevronLeft className="h-4 w-4" /> Back</button>
        <div className="flex flex-col gap-3">
          {(view.items || []).map((m) => (
            <div key={m.id} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
              <div className={cn("max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5", m.role === "user" ? "bg-foreground text-background" : "bg-secondary")}>{m.text}</div>
            </div>
          ))}
        </div>
      </Shell>
    );

  if (view.mode === "activity")
    return (
      <Shell title={view.user?.name || "Activity"} onSignOut={signOut}>
        <button className="mb-4 flex items-center gap-1 text-sm" onClick={() => setView({ mode: "home" })}><ChevronLeft className="h-4 w-4" /> Back</button>
        {(!view.items || !view.items.length) ? <p className="text-muted-foreground">No conversations yet.</p> :
          view.items.map((c) => (
            <button key={c.id} data-testid={`parent-chat-${c.id}`} onClick={() => openChat(view.user, c)} className="flex w-full items-center gap-3 border-b border-border py-3 text-left">
              <span className="text-xs font-bold uppercase text-muted-foreground">{c.tab}</span>
              <span className="flex-1 truncate">{c.title}</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          ))}
      </Shell>
    );

  const users = data?.linked_users || [];
  return (
    <Shell title="Parent" onSignOut={signOut}>
      <p className="mb-4 text-sm text-muted-foreground">{account?.email}</p>

      <div className="mb-6 rounded-xl border border-border p-5">
        <div className="mb-2 flex items-center justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Add a child</div>
            <p className="mt-1 text-sm text-muted-foreground">Create your child's account and login. They sign in on their own device with the email and password you set.</p>
          </div>
          <Button variant="secondary" className="py-1.5 text-sm" data-testid="add-child-toggle" onClick={() => { setAddOpen((o) => !o); setCards([]); setCardErrors([]); }}><Plus className="h-4 w-4" /> Add child</Button>
        </div>
        {addOpen ? (
          <div className="mt-3 flex flex-col gap-3">
            <Field label="Child's name" value={child.name} onChange={(e) => setChild((c) => ({ ...c, name: e.target.value }))} placeholder="e.g. Emma" data-testid="child-name" />
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-muted-foreground">Learning Level</span>
              <select data-testid="child-level" value={child.learning_level} onChange={(e) => setChild((c) => ({ ...c, learning_level: e.target.value }))}
                className="rounded-xl border border-input bg-background px-4 py-3 text-[15px]">
                <option value="">Choose a level</option>
                {LEARNING_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <Field label="Child's login email" type="email" value={child.email} onChange={(e) => setChild((c) => ({ ...c, email: e.target.value }))} placeholder="child@email.com" data-testid="child-email" />
            <Field label="Password" type="password" value={child.password} onChange={(e) => setChild((c) => ({ ...c, password: e.target.value }))} placeholder="At least 6 characters" data-testid="child-password" />
            <Field label="Coupon code (optional)" value={child.coupon_code} onChange={(e) => setChild((c) => ({ ...c, coupon_code: e.target.value }))} placeholder="e.g. MAX20" data-testid="child-coupon" />
            <div className="flex flex-wrap gap-2">
              <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button variant="secondary" className="flex-1" data-testid="create-child-another" onClick={() => createChild(true)}>Save & add another</Button>
              <Button className="flex-1" data-testid="create-child" onClick={() => createChild(false)}>Save & done</Button>
            </div>
          </div>
        ) : null}
      </div>

      {cardsOpen && cards.length ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="login-cards-modal">
          <div className="flex max-h-[85vh] w-full max-w-md flex-col gap-3 overflow-auto rounded-2xl bg-background p-5">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Login details</h3>
              <button data-testid="close-login-cards" onClick={() => { setCardsOpen(false); setCards([]); setCardErrors([]); }}><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-muted-foreground">Share each card with your child to set up their device. Passwords are shown only here — copy or share them now.</p>
            {cards.map((c, i) => (
              <div key={i} data-testid={`login-card-${i}`} className="flex gap-3 rounded-xl border border-border p-4">
                <div className="flex flex-1 flex-col gap-1">
                  <div className="font-semibold">{c.name} <span className="text-xs font-normal text-muted-foreground">· {c.learning_level}</span></div>
                  <div className="text-sm">Email: <span className="font-mono">{c.email}</span></div>
                  {c.password ? <div className="text-sm">Password: <span className="font-mono">{c.password}</span></div> : <div className="text-xs text-muted-foreground">Single-use QR — scan to sign in.</div>}
                  <div className="flex gap-2 pt-2">
                    <Button variant="secondary" className="py-1.5 text-sm" data-testid={`copy-login-${i}`} onClick={() => copyCard(c)}>Copy</Button>
                    <Button variant="secondary" className="py-1.5 text-sm" data-testid={`share-login-${i}`} onClick={() => shareCard(c)}>Share</Button>
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-center gap-1">
                  <QRImage value={loginUrl(c)} size={96} />
                  <span className="text-[10px] text-muted-foreground">Scan to log in</span>
                </div>
              </div>
            ))}
            {cardErrors.length ? <div className="rounded-lg bg-secondary p-3 text-sm">{cardErrors.map((e, i) => <div key={i} data-testid={`card-error-${i}`}>{e}</div>)}</div> : null}
            <Button variant="secondary" data-testid="print-sheet" onClick={printSheet}><Printer className="h-4 w-4" /> Print / save sheet</Button>
            {cards.length > 1 ? <Button variant="secondary" data-testid="copy-all-logins" onClick={copyAllCards}>Copy all logins</Button> : null}
            <Button data-testid="login-cards-done" onClick={() => { setCardsOpen(false); setCards([]); setCardErrors([]); }}>Done</Button>
          </div>
        </div>
      ) : null}

      {editUser ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="edit-child-modal">
          <div className="flex w-full max-w-sm flex-col gap-3 rounded-2xl bg-background p-5">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Edit {editUser.name}</h3>
              <button data-testid="close-edit-child" onClick={() => setEditUser(null)}><X className="h-5 w-5" /></button>
            </div>
            <Field label="Name" value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} data-testid="edit-child-name" />
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-muted-foreground">Learning Level</span>
              <select data-testid="edit-child-level" value={editForm.learning_level} onChange={(e) => setEditForm((f) => ({ ...f, learning_level: e.target.value }))}
                className="rounded-xl border border-input bg-background px-4 py-3 text-[15px]">
                <option value="">Choose a level</option>
                {LEARNING_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="ghost" className="flex-1" onClick={() => setEditUser(null)}>Cancel</Button>
              <Button className="flex-1" data-testid="save-edit-child" onClick={saveEdit}>Save</Button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="mb-6 rounded-xl border border-border p-5">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Link an existing child</div>
        <p className="mt-1 text-sm text-muted-foreground">Already have a child using MAXx on their own device? In the MAXx app they tap <b>Scan</b> and point their camera at this Link QR to connect to you.</p>
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <div data-testid="parent-link-qr" className="rounded-lg bg-white p-2">
            {data?.link_token ? <QRImage value={linkUrl(data.link_token)} size={128} /> : <div className="h-32 w-32" />}
          </div>
          <Button variant="secondary" className="py-1.5 text-sm" data-testid="regen-link-qr" onClick={regenLinkQr}>New QR</Button>
        </div>
      </div>

      <h2 className="mb-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">Linked users</h2>
      {users.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-border py-12 text-center">
          <Users className="h-7 w-7 text-muted-foreground" />
          <p className="text-muted-foreground">No users are linked to your account yet.</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {users.map((u) => (
            <div key={u.id} className="flex flex-col gap-3 rounded-xl border border-border p-5">
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold">{u.name}</span>
                <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold">{u.entitlement?.state}{u.entitlement?.days_left != null ? ` · ${u.entitlement.days_left}d` : ""}</span>
              </div>
              <span className="text-sm text-muted-foreground">Learning Level: {u.learning_level}</span>
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" className="flex-1 py-2 text-sm" data-testid={`view-activity-${u.id}`} onClick={() => openActivity(u)}>View activity</Button>
                <Button className="flex-1 py-2 text-sm" data-testid={`pay-child-${u.id}`} onClick={() => payChild(u)}>{u.entitlement?.state === "active" ? "Renew / manage" : "Subscribe / Pay"}</Button>
              </div>
              {u.quick_login_token ? (
                <div data-testid={`child-access-qr-${u.id}`} className="mt-1 flex items-center gap-3 rounded-lg border border-border p-3">
                  <div className="shrink-0 rounded-md bg-white p-1.5"><QRImage value={accessUrl(u.quick_login_token)} size={80} /></div>
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="text-sm font-semibold">Kid access QR</span>
                    <span className="text-xs text-muted-foreground">Scan in the MAXx app to sign {u.name} in on any device — no password.</span>
                    <button data-testid={`regen-access-${u.id}`} onClick={() => regenAccessQr(u)} className="self-start text-xs text-muted-foreground underline">New access QR</button>
                  </div>
                </div>
              ) : null}
              <button data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)} className="text-left text-sm text-muted-foreground">Edit name / level</button>
              <button data-testid={`remove-user-${u.id}`} onClick={() => remove(u)} className="text-left text-sm text-muted-foreground">Delete this account</button>
              <button data-testid={`unlink-user-${u.id}`} onClick={() => unlink(u)} className="text-left text-sm text-muted-foreground">Remove link (keep their account)</button>
            </div>
          ))}
        </div>
      )}
    </Shell>
  );
}

function AffiliateDash() {
  const { account } = useAuth();
  const signOut = useSignOut();
  const [data, setData] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [mainTab, setMainTab] = useState("overview");
  const [tab, setTab] = useState("active");
  const [refs, setRefs] = useState(null);
  const [notifs, setNotifs] = useState(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
  const earnQs = (f, t) => { const q = []; if (f) q.push(`from=${f}`); if (t) q.push(`to=${t}`); return q.length ? `?${q.join("&")}` : ""; };
  const loadEarnings = async (f, t) => { try { setEarnings(await apiFetch(`/affiliate/earnings${earnQs(f, t)}`)); } catch (e) { toast(e.message); } };
  const loadNotifs = async () => { try { setNotifs(await apiFetch("/affiliate/notifications")); } catch { /* ignore */ } };
  useEffect(() => {
    (async () => { try { setData(await apiFetch("/affiliate/me")); } catch (e) { toast(e.message); } })();
    loadEarnings(); loadNotifs();
    const t = setInterval(loadNotifs, 30000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { setRefs(null); (async () => { try { setRefs(await apiFetch(`/affiliate/referrals?tab=${tab}`)); } catch (e) { toast(e.message); } })(); }, [tab]);
  const applyFilter = (f, t) => { setFilterFrom(f); setFilterTo(t); loadEarnings(f || undefined, t || undefined); };
  const openNotifs = async () => {
    const willOpen = !notifOpen;
    setNotifOpen(willOpen);
    if (willOpen && notifs?.unread_count) {
      try { await apiFetch("/affiliate/notifications/read", { method: "POST" }); setNotifs((n) => ({ ...n, unread_count: 0, notifications: (n.notifications || []).map((x) => ({ ...x, read: true })) })); } catch { /* ignore */ }
    }
  };
  const stat = (label, value, testId) => (
    <div data-testid={testId} className="flex-1 rounded-xl border border-border p-5"><div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</div><div className="mt-1 text-2xl font-bold">{value}</div></div>
  );
  const statusStyle = (s) => s === "Subscribed" ? "bg-primary/10 text-primary" : s === "Cancelled" ? "bg-destructive/10 text-destructive" : "bg-secondary text-muted-foreground";
  return (
    <Shell title="Affiliate" onSignOut={signOut}>
      {!data ? <div className="flex justify-center py-16"><Spinner className="h-6 w-6 text-foreground" /></div> : (
        <div className="flex flex-col gap-5">
          <div><h2 className="text-2xl font-bold">{data.name}</h2><p className="text-sm text-muted-foreground">{account?.email}</p></div>

          {/* Top tabs */}
          <div className="flex items-center justify-between border-b border-border">
            <div className="flex gap-1">
              {[["overview", "Overview"], ["earnings", "Earnings"]].map(([k, l]) => (
                <button key={k} data-testid={`aff-tab-${k}`} onClick={() => setMainTab(k)}
                  className={cn("-mb-px border-b-2 px-4 py-2 text-sm font-semibold transition-colors", mainTab === k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}>{l}</button>
              ))}
            </div>
            <div className="relative pb-1">
              <button data-testid="aff-notif-bell" onClick={openNotifs} className="relative rounded-full p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
                <Bell className="h-5 w-5" />
                {notifs?.unread_count ? <span data-testid="aff-notif-badge" className="absolute right-0 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-white">{notifs.unread_count}</span> : null}
              </button>
              {notifOpen ? (
                <div data-testid="aff-notif-panel" className="absolute right-0 z-20 mt-1 w-80 max-w-[85vw] overflow-hidden rounded-xl border border-border bg-background shadow-lg">
                  <div className="flex items-center justify-between border-b border-border px-3 py-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Notifications</span>
                    <button data-testid="aff-notif-close" onClick={() => setNotifOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
                  </div>
                  <div className="max-h-80 overflow-y-auto p-1">
                    {(notifs?.notifications || []).length === 0 ? (
                      <p className="p-3 text-sm text-muted-foreground">No notifications yet. You'll be alerted here when a monthly payout is marked paid.</p>
                    ) : notifs.notifications.map((n, i) => (
                      <div key={n.id} data-testid={`aff-notif-${i}`} className={cn("rounded-lg p-3", n.read ? "" : "bg-primary/5")}>
                        <div className="text-sm font-semibold">{n.title}</div>
                        <div className="text-sm text-muted-foreground">{n.body}</div>
                        <div className="mt-1 text-xs text-muted-foreground">{fmtDate(n.created_at)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {mainTab === "overview" ? (
            <>
              <div className="flex flex-col gap-3 rounded-xl border border-border p-5" data-testid="aff-coupon-card">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Your coupon code</div>
                <div className="flex items-center justify-between gap-3">
                  <span data-testid="aff-my-coupon" className="text-2xl font-bold tracking-wider">{data.coupon_code || "—"}</span>
                  <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize", data.status === "active" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground")}>{data.status}</span>
                </div>
                <div data-testid="aff-my-plan" className="text-sm text-muted-foreground">Commission plan: <span className="font-semibold text-foreground">{data.plan.plan_label}</span> — {data.plan.recurring_enabled ? `recurring up to ${data.plan.max_billing_cycles} billing cycles` : "one-time"}</div>
                <div data-testid="aff-my-commission-type" className="text-sm text-muted-foreground">Commission type: <span className="font-semibold text-foreground">{data.commission_type === "perpetual" ? "Perpetual" : "6-month"}</span></div>
                {data.phone ? <div data-testid="aff-my-phone" className="text-sm text-muted-foreground">Phone: <span className="font-semibold text-foreground">{data.phone}</span></div> : null}
              </div>

              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {stat("Subscribed", data.dashboard.active_referrals)}
                {stat("Archived", data.dashboard.completed_referrals)}
                {stat("This month", money(data.dashboard.this_month_commission))}
                {stat("Lifetime paid", money(data.dashboard.lifetime_paid))}
              </div>

              <div className="flex items-center gap-2">
                <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Your referrals</h2>
                <div className="flex-1" />
                {[["active", "Subscribed"], ["archive", "Archive"]].map(([k, l]) => (
                  <button key={k} data-testid={`aff-ref-tab-${k}`} onClick={() => setTab(k)}
                    className={cn("rounded-full border px-3 py-1 text-xs font-semibold", tab === k ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground")}>{l}</button>
                ))}
              </div>
              {!refs ? <div className="flex justify-center py-6"><Spinner className="h-5 w-5 text-foreground" /></div> : refs.referrals.length === 0 ? (
                <p className="text-sm text-muted-foreground">{tab === "active" ? "No active referrals yet. When someone subscribes with your coupon code, they appear here (anonymized) with their commission progress." : "No archived referrals yet."}</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {refs.referrals.map((r, i) => (
                    <div key={i} data-testid={`aff-referral-${i}`} className="flex items-center justify-between gap-3 rounded-xl border border-border p-4">
                      <div className="min-w-0">
                        <div className="font-mono font-semibold">{r.referral}</div>
                        <div className="truncate text-sm text-muted-foreground">joined {fmtDate(r.joined)} · {r.progress} · earned {money(r.earned)}</div>
                      </div>
                      <span className="shrink-0 rounded-full bg-secondary px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">{tab === "archive" ? r.result : r.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            !earnings ? <div className="flex justify-center py-10"><Spinner className="h-6 w-6 text-foreground" /></div> : (
              <div className="flex flex-col gap-5">
                <div className="flex items-center justify-between">
                  <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Earnings statement</h2>
                  <Button data-testid="aff-download-earnings" variant="secondary" className="py-1.5 text-sm" onClick={() => downloadFile(`/affiliate/earnings.csv${earnQs(filterFrom, filterTo)}`, "my_earnings.csv").catch((e) => toast(e.message))}><Download className="h-4 w-4" /> Download CSV</Button>
                </div>

                {/* Month / range filter */}
                <AffiliateRangeFilter from={filterFrom} to={filterTo} onChange={applyFilter} />
                {earnings.filter?.active ? (
                  <div data-testid="aff-filter-banner" className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
                    Showing <span className="font-semibold">{fmtMonth(earnings.filter.from) || "start"}</span> – <span className="font-semibold">{fmtMonth(earnings.filter.to) || "now"}</span> · Period earned <span className="font-semibold">{money(earnings.summary?.period_earned)}</span>
                  </div>
                ) : null}

                {/* Referral status counts */}
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {stat("Subscribed", earnings.summary?.subscribed ?? 0, "aff-earn-subscribed")}
                  {stat("Cancelled", earnings.summary?.cancelled ?? 0, "aff-earn-cancelled")}
                  {stat("Archived", earnings.summary?.completed ?? 0, "aff-earn-completed")}
                  {stat("Total referrals", earnings.summary?.total_referrals ?? 0, "aff-earn-total")}
                </div>

                {/* Money summary */}
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {stat("This month due", money(earnings.summary?.this_month_due), "aff-earn-due")}
                  {stat("Awaiting payment", money(earnings.summary?.awaiting_payment), "aff-earn-awaiting")}
                  {stat("Lifetime earned", money(earnings.summary?.lifetime_earned), "aff-earn-lifetime-earned")}
                  {stat("Lifetime paid", money(earnings.summary?.lifetime_paid), "aff-earn-lifetime-paid")}
                </div>

                <p data-testid="aff-earn-plan" className="text-sm text-muted-foreground">
                  Commission rate <span className="font-semibold text-foreground">{Math.round((earnings.summary?.commission_rate || 0) * 100)}%</span> · Subscription price <span className="font-semibold text-foreground">{money(earnings.summary?.subscription_price)}</span> · Plan {earnings.summary?.plan_label}
                </p>

                <div data-testid="aff-earnings-this-month" className="flex items-center justify-between rounded-xl border border-primary/40 bg-primary/5 p-4 text-sm">
                  <span className="font-semibold">This month ({fmtMonth(earnings.this_month.month)})</span>
                  <span>{money(earnings.this_month.amount)} · {earnings.this_month.status}</span>
                </div>

                {/* Per-referral statement */}
                <div>
                  <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Per-referral statement</h3>
                  {(earnings.referrals || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">No referrals yet. When someone subscribes with your coupon code, they appear here (anonymized).</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {earnings.referrals.map((r, i) => (
                        <div key={i} data-testid={`aff-statement-${i}`} className="flex items-center justify-between gap-3 rounded-xl border border-border p-4">
                          <div className="min-w-0">
                            <div className="font-mono font-semibold">{r.referral}</div>
                            <div className="truncate text-sm text-muted-foreground">joined {fmtDate(r.joined)} · {r.progress}{r.ended ? ` · ended ${fmtDate(r.ended)}` : ""} · earned {money(r.earned)}</div>
                          </div>
                          <span className={cn("shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold", statusStyle(r.status))}>{r.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Monthly earnings history */}
                <div>
                  <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Monthly earnings</h3>
                  {earnings.history.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No earnings history yet. Recorded commissions appear here monthly as your referred subscriptions stay active.</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {earnings.history.map((p, i) => (
                        <div key={i} data-testid={`aff-payout-${i}`} className="flex items-center justify-between rounded-xl border border-border p-4 text-sm">
                          <span>{fmtMonth(p.month)}</span>
                          <span className="text-muted-foreground">{money(p.amount)} · {p.status}{p.paid_date ? ` · ${fmtDate(p.paid_date)}` : ""}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )
          )}
        </div>
      )}
    </Shell>
  );
}

const SECTIONS = ["Users", "Usage", "Affiliates", "Schools", "Trials", "Reports", "Prompts"];
const PTABS = ["everyday", "learn", "create", "research"];
const USER_FILTERS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "trial", label: "Free trial" },
  { key: "expired", label: "Expired" },
  { key: "has_parent", label: "Has parent" },
];

function DeveloperDash() {
  const signOut = useSignOut();
  const [section, setSection] = useState("Users");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [userQ, setUserQ] = useState("");
  const [userFilter, setUserFilter] = useState("all");
  const [userCounts, setUserCounts] = useState({});
  const [detail, setDetail] = useState(null);
  const [usage, setUsage] = useState(null);
  const [usageRange, setUsageRange] = useState("month");
  const [usageStart, setUsageStart] = useState("");
  const [usageEnd, setUsageEnd] = useState("");
  const [pricing, setPricing] = useState(null);
  const [pricingReviewedAt, setPricingReviewedAt] = useState(null);
  const [flatMargin, setFlatMargin] = useState("60");
  const [reconcile, setReconcile] = useState(null);
  const [budgetInput, setBudgetInput] = useState("");
  const [userDetail, setUserDetail] = useState(null);
  const [allUsers, setAllUsers] = useState(null);
  const [usersView, setUsersView] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [userSort, setUserSort] = useState("cost");
  const [socialReports, setSocialReports] = useState([]);
  const [modMenu, setModMenu] = useState(null);
  const [modHistory, setModHistory] = useState(null);
  const [prompts, setPrompts] = useState(null);

  const load = useCallback(async (sec) => {
    setLoading(true);
    try {
      if (sec === "Users") {
        const params = new URLSearchParams();
        if (userQ) params.set("q", userQ);
        if (userFilter && userFilter !== "all") params.set("filter", userFilter);
        const d = await apiFetch(`/developer/users${params.toString() ? `?${params.toString()}` : ""}`);
        setUsers(d.users); setUserCounts(d.counts || {});
      } else if (sec === "Usage") {
        const p = new URLSearchParams({ range: usageRange });
        if (usageRange === "custom") { if (usageStart) p.set("start", usageStart); if (usageEnd) p.set("end", usageEnd); }
        setUsage(await apiFetch(`/developer/reports/usage?${p.toString()}`));
        const pr = await apiFetch("/developer/config/token-pricing");
        setPricing((prev) => prev || pr.models);
        setPricingReviewedAt(pr.reviewed_at || null);
        const bg = await apiFetch("/developer/config/budget");
        setBudgetInput(bg.monthly_usd ? String(bg.monthly_usd) : "");
        try { setReconcile(await apiFetch(`/developer/reports/reconcile?${p.toString()}`)); } catch { setReconcile(null); }
      } else if (sec === "Reports") setSocialReports((await apiFetch("/developer/social/reports")).reports);
      else if (sec === "Prompts") setPrompts((await apiFetch("/developer/config/starter-prompts")).prompts);
    } catch (e) { toast(e.message); } finally { setLoading(false); }
  }, [userQ, userFilter, usageRange, usageStart, usageEnd]);
  useEffect(() => { load(section); setDetail(null); setUserDetail(null); setUsersView(null); /* eslint-disable-next-line */ }, [section]);
  useEffect(() => { if (section === "Users") load("Users"); /* eslint-disable-next-line */ }, [userFilter]);
  useEffect(() => { if (section === "Usage") { setUserDetail(null); setUsersView(null); load("Usage"); } /* eslint-disable-next-line */ }, [usageRange]);

  const openUser = async (id) => { try { setDetail(await apiFetch(`/developer/users/${id}`)); } catch (e) { toast(e.message); } };
  const overrideSub = async (id, status) => { try { await apiFetch(`/developer/users/${id}/subscription`, { method: "POST", body: { status } }); toast(`Set to ${status}.`); openUser(id); } catch (e) { toast(e.message); } };
  const savePricing = async () => { try { const r = await apiFetch("/developer/config/token-pricing", { method: "PUT", body: { models: pricing } }); setPricing(r.models); if (r.reviewed_at) setPricingReviewedAt(r.reviewed_at); toast("Pricing saved."); } catch (e) { toast(e.message); } };
  const markPricingReviewed = async () => { try { const r = await apiFetch("/developer/config/token-pricing/reviewed", { method: "POST" }); setPricingReviewedAt(r.reviewed_at); toast("Marked reviewed today."); } catch (e) { toast(e.message); } };
  const syncPricing = async () => { try { const r = await apiFetch("/developer/config/published-rates"); setPricing((prev) => { const merged = { ...(prev || {}) }; Object.keys(r.models).forEach((m) => { merged[m] = { ...(merged[m] || {}), ...r.models[m] }; }); return merged; }); toast(`Filled from OpenAI reference (as of ${r.as_of}). Review and Save.`); } catch (e) { toast(e.message); } };
  const refreshReconcile = async () => { const p = usageQS(); try { setReconcile(await apiFetch(`/developer/reports/reconcile?${p.toString()}`)); toast("Pulled OpenAI actuals."); } catch (e) { toast(e.message); } };
  const exportUsage = () => { const p = new URLSearchParams({ range: usageRange }); if (usageRange === "custom") { if (usageStart) p.set("start", usageStart); if (usageEnd) p.set("end", usageEnd); } downloadFile(`/developer/reports/usage.csv?${p.toString()}`, "usage_report.csv").catch((e) => toast(e.message)); };
  const saveBudget = async () => { const v = parseFloat(budgetInput || "0"); if (isNaN(v) || v < 0) return toast("Enter 0 or more."); try { await apiFetch("/developer/config/budget", { method: "PUT", body: { monthly_usd: v } }); toast("Budget saved."); load("Usage"); } catch (e) { toast(e.message); } };
  const usageQS = () => { const p = new URLSearchParams({ range: usageRange }); if (usageRange === "custom") { if (usageStart) p.set("start", usageStart); if (usageEnd) p.set("end", usageEnd); } return p; };
  const openUserDetail = async (uid) => { try { setUserDetail(await apiFetch(`/developer/reports/users/${uid}?${usageQS()}`)); } catch (e) { toast(e.message); } };
  const loadAllUsers = async (sort = userSort, q = userSearch) => { try { const p = usageQS(); p.set("sort", sort); if (q) p.set("q", q); const d = await apiFetch(`/developer/reports/users?${p}`); setAllUsers(d); setUsersView("all"); } catch (e) { toast(e.message); } };
  const exportAllUsers = () => { const p = usageQS(); p.set("sort", userSort); if (userSearch) p.set("q", userSearch); downloadFile(`/developer/reports/users.csv?${p}`, "usage_by_user.csv").catch((e) => toast(e.message)); };
  const exportStatement = (uid, nm) => { downloadFile(`/developer/reports/users/${uid}.csv?${usageQS()}`, `statement_${(nm || uid).replace(/\s+/g, "_")}.csv`).catch((e) => toast(e.message)); };
  const resolveReport = async (id) => { try { await apiFetch(`/developer/social/reports/${id}/resolve`, { method: "POST", body: { status: "reviewed" } }); toast("Marked reviewed."); load("Reports"); } catch (e) { toast(e.message); } };
  const moderate = async (offenderId, action, days) => { if (!offenderId) return toast("No user to action on this report."); let reason; if (action !== "reinstate") { reason = window.prompt(`Optional note the user will see for "${action}":`, "") || undefined; } try { await apiFetch(`/developer/users/${offenderId}/moderate`, { method: "POST", body: { action, days, reason } }); setModMenu(null); toast(action === "warn" ? "User warned." : action === "ban" ? "User banned from group chats." : action === "reinstate" ? "User reinstated." : `User suspended ${days} days.`); if (modHistory && modHistory.user_id === offenderId) { try { setModHistory(await apiFetch(`/developer/users/${offenderId}/moderation-history`)); } catch {} } load("Reports"); } catch (e) { toast(e.message); } };
  const viewHistory = async (userId) => { if (!userId) return; try { setModHistory(await apiFetch(`/developer/users/${userId}/moderation-history`)); } catch (e) { toast(e.message); } };
  const savePrompts = async () => { try { await apiFetch("/developer/config/starter-prompts", { method: "PUT", body: { prompts } }); toast("Starter prompts saved."); } catch (e) { toast(e.message); } };

  const card = "rounded-xl border border-border p-5";

  return (
    <Shell title="Developer" onSignOut={signOut}>
      <div className="mb-5 flex flex-wrap gap-2">
        {SECTIONS.map((s) => (
          <button key={s} data-testid={`dev-section-${s}`} onClick={() => setSection(s)}
            className={cn("rounded-full border px-4 py-2 text-sm font-semibold", section === s ? "border-primary bg-primary text-primary-foreground" : "border-border")}>{s}</button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-16"><Spinner className="h-6 w-6 text-foreground" /></div> : (
        <>
          {section === "Users" && !detail ? (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap gap-2">
                {USER_FILTERS.map((f) => (
                  <button key={f.key} data-testid={`user-filter-${f.key}`} onClick={() => setUserFilter(f.key)}
                    className={cn("rounded-full border px-3 py-1.5 text-xs font-semibold", userFilter === f.key ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground")}>
                    {f.label}{userCounts[f.key] !== undefined ? ` (${userCounts[f.key]})` : ""}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <input data-testid="dev-user-search" value={userQ} onChange={(e) => setUserQ(e.target.value)} placeholder="Search by name" className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none" />
                <Button variant="secondary" className="py-2 text-sm" onClick={() => load("Users")}>Search</Button>
              </div>
              {users.map((u) => (
                <button key={u.id} data-testid={`dev-user-${u.id}`} onClick={() => openUser(u.id)} className={cn(card, "flex items-center justify-between text-left")}>
                  <div><div className="font-semibold">{u.name}</div><div className="text-sm text-muted-foreground">{u.learning_level} · {u.subscription}{u.has_parent ? " · has parent" : ""}</div></div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </button>
              ))}
              {users.length === 0 ? <p className="text-muted-foreground">No users found.</p> : null}
            </div>
          ) : null}

          {section === "Users" && detail ? (
            <div className="flex flex-col gap-4">
              <button className="flex items-center gap-1 text-sm" onClick={() => setDetail(null)}><ChevronLeft className="h-4 w-4" /> Back</button>
              <div className={cn(card, "flex flex-col gap-1.5")}>
                <div className="text-xl font-bold">{detail.user.name}</div>
                <div className="text-sm text-muted-foreground">Learning Level: {detail.user.learning_level}</div>
                <div className="text-sm text-muted-foreground">Chats: {detail.chat_count}</div>
                <div className="text-sm text-muted-foreground">Subscription: {detail.subscription}</div>
                <div className="text-sm text-muted-foreground">Linked parent: {detail.parent?.email || "none"}</div>
                <div className="mt-2 text-xs font-bold uppercase text-muted-foreground">Override subscription (demo)</div>
                <div className="flex gap-2">
                  {["trialing", "active", "expired"].map((st) => <Button key={st} data-testid={`dev-set-${st}`} variant="secondary" className="flex-1 py-2 text-sm" onClick={() => overrideSub(detail.user.id, st)}>{st}</Button>)}
                </div>
              </div>
            </div>
          ) : null}

          {section === "Affiliates" ? <AffiliateCenter /> : null}
          {section === "Schools" ? <SchoolCenter /> : null}
          {section === "Trials" ? <TrialCenter /> : null}

          {section === "Usage" ? (
            userDetail ? (
              <UserDetailPanel detail={userDetail} card={card} onBack={() => setUserDetail(null)}
                onHistory={() => viewHistory(userDetail.user_id)}
                onExport={() => exportStatement(userDetail.user_id, userDetail.name)} />
            ) : usersView === "all" ? (
              <AllUsersPanel data={allUsers} card={card} search={userSearch} setSearch={setUserSearch}
                sort={userSort} onSort={(s) => { setUserSort(s); loadAllUsers(s); }}
                onSearch={() => loadAllUsers(userSort, userSearch)} onSelect={openUserDetail}
                onExport={exportAllUsers} onBack={() => setUsersView(null)} />
            ) : (
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center gap-2">
                {[["day", "Today"], ["week", "7 days"], ["month", "30 days"], ["custom", "Custom"]].map(([k, l]) => (
                  <button key={k} data-testid={`usage-range-${k}`} onClick={() => setUsageRange(k)}
                    className={cn("rounded-full border px-3 py-1.5 text-xs font-semibold", usageRange === k ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground")}>{l}</button>
                ))}
                <div className="flex-1" />
                <Button data-testid="export-usage-csv" variant="secondary" className="py-1.5 text-sm" onClick={exportUsage}><Download className="h-4 w-4" /> CSV</Button>
              </div>
              {usageRange === "custom" ? (
                <div className="flex flex-wrap items-end gap-2">
                  <div><label className="text-xs text-muted-foreground">From</label><input data-testid="usage-start" type="date" value={usageStart} onChange={(e) => setUsageStart(e.target.value)} className="mt-1 block rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none" /></div>
                  <div><label className="text-xs text-muted-foreground">To</label><input data-testid="usage-end" type="date" value={usageEnd} onChange={(e) => setUsageEnd(e.target.value)} className="mt-1 block rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none" /></div>
                  <Button data-testid="usage-apply" variant="secondary" className="py-2 text-sm" onClick={() => load("Usage")}>Apply</Button>
                </div>
              ) : null}
              {usage ? (
                <>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
                    <div className={card}><div className="text-xs font-bold uppercase text-muted-foreground">Modeled cost</div><div data-testid="usage-total-cost" className="mt-1 text-2xl font-bold">${usage.totals.cost.toFixed(4)}</div><div className="text-[10px] text-muted-foreground">from your pricing</div></div>
                    <div className={cn(card, "ring-1 ring-foreground/20")}><div className="text-xs font-bold uppercase text-muted-foreground">Avg / user</div><div data-testid="usage-avg-cost" className="mt-1 text-2xl font-bold">${(usage.avg_cost_per_user || 0).toFixed(4)}</div><div className="text-[10px] text-muted-foreground">{usage.active_users} active users</div></div>
                    <div className={card}><div className="text-xs font-bold uppercase text-muted-foreground">Calls</div><div className="mt-1 text-2xl font-bold">{usage.totals.calls}</div></div>
                    <div className={card}><div className="text-xs font-bold uppercase text-muted-foreground">Total tokens</div><div className="mt-1 text-2xl font-bold">{usage.totals.total_tokens.toLocaleString()}</div></div>
                    <div className={card}><div className="text-xs font-bold uppercase text-muted-foreground">In / Out</div><div className="mt-1 text-sm font-semibold">{usage.totals.input_tokens.toLocaleString()} / {usage.totals.output_tokens.toLocaleString()}</div></div>
                  </div>
                  {usage.budget > 0 ? (
                    <div data-testid="budget-status" className={cn("rounded-xl border p-4 text-sm", usage.over_budget ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground")}>
                      Month-to-date spend: <span className="font-bold">${usage.mtd_cost.toFixed(4)}</span> of ${usage.budget.toFixed(2)} monthly budget{usage.over_budget ? " — over budget! Modeled spend has crossed your limit." : ""}
                    </div>
                  ) : null}
                  <CostTrendChart daily={usage.daily} card={card} />
                  <p className="text-xs text-muted-foreground">Modeled cost = exact usage × your pricing below. Text tokens are exact (from the API); voice is billed by audio-seconds (Whisper) and characters (TTS); Research adds the OpenAI web_search per-call fee. Demo/test accounts are excluded.</p>
                  <UsageTable title="By model" rows={usage.by_model} card={card} />
                  <UsageTable title="By tab / feature" rows={usage.by_tab} card={card} />
                  <TopUsersTable users={usage.top_users} card={card} onSelect={openUserDetail} onViewAll={() => loadAllUsers()} />
                  <UsageBreakdowns qs={usageQS().toString()} card={card} />
                  <div className={cn(card, "flex flex-col gap-3")} data-testid="flatprice-card">
                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Flat-price helper</div>
                    <p className="text-[11px] text-muted-foreground">Break-even = your avg modeled cost per active user for this range. Pick a markup to see a suggested flat monthly price.</p>
                    <div className="flex flex-wrap items-end gap-5">
                      <div><div className="text-xs uppercase text-muted-foreground">Break-even / user</div><div className="mt-1 text-xl font-bold" data-testid="flat-breakeven">${(usage.avg_cost_per_user || 0).toFixed(4)}</div></div>
                      <label className="flex flex-col text-xs text-muted-foreground">Markup %
                        <input data-testid="flat-margin" type="number" min="0" step="5" value={flatMargin} onChange={(e) => setFlatMargin(e.target.value)} className="mt-1 w-24 rounded-lg border border-input bg-background px-2 py-1.5 text-sm outline-none" />
                      </label>
                      <div><div className="text-xs uppercase text-muted-foreground">Suggested price</div><div className="mt-1 text-2xl font-bold" data-testid="flat-suggested">${((usage.avg_cost_per_user || 0) * (1 + (parseFloat(flatMargin) || 0) / 100)).toFixed(2)}</div><div className="text-[10px] text-muted-foreground">/ user / month</div></div>
                    </div>
                    <p className="text-[10px] text-muted-foreground">Tip: use the 30-day range for a stable monthly figure, then add headroom for heavy users and payment fees.</p>
                  </div>
                  <div className={cn(card, "flex flex-col gap-3")} data-testid="reconcile-card">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Reconcile vs OpenAI actual</div>
                      <Button variant="secondary" className="py-1.5 text-sm" data-testid="reconcile-refresh" onClick={refreshReconcile}>Pull actuals</Button>
                    </div>
                    {!reconcile ? (
                      <p className="text-sm text-muted-foreground">Loading…</p>
                    ) : reconcile.configured === false ? (
                      <p className="text-sm text-muted-foreground" data-testid="reconcile-unconfigured">Add your OpenAI <span className="font-semibold">Admin key</span> (read-only, scope <span className="font-mono">api.usage.read</span>) to compare your modeled cost against OpenAI's real billed spend. It's separate from your model API key and reading it doesn't consume tokens.</p>
                    ) : reconcile.error ? (
                      <p className="text-sm text-destructive" data-testid="reconcile-error">Couldn't reach the OpenAI Costs API: {reconcile.error}</p>
                    ) : (
                      <>
                        <div className="grid grid-cols-3 gap-3">
                          <div className={card}><div className="text-xs uppercase text-muted-foreground">Modeled (all)</div><div className="mt-1 text-xl font-bold" data-testid="reconcile-modeled">${(reconcile.modeled_cost || 0).toFixed(4)}</div></div>
                          <div className={card}><div className="text-xs uppercase text-muted-foreground">OpenAI actual</div><div className="mt-1 text-xl font-bold" data-testid="reconcile-actual">${(reconcile.actual_cost || 0).toFixed(4)}</div></div>
                          <div className={cn(card, Math.abs(reconcile.drift_pct || 0) > 10 ? "ring-1 ring-foreground" : "")}><div className="text-xs uppercase text-muted-foreground">Drift</div><div className="mt-1 text-xl font-bold" data-testid="reconcile-drift">{reconcile.drift_pct == null ? "—" : `${reconcile.drift_pct > 0 ? "+" : ""}${reconcile.drift_pct}%`}</div></div>
                        </div>
                        <p className="text-[11px] text-muted-foreground">Modeled here counts ALL usage (test accounts included) for a fair match. A large drift means your pricing table has drifted from OpenAI's real rates — update it below. OpenAI actuals include every charge on the key, and the most recent day may still be finalizing.</p>
                      </>
                    )}
                  </div>
                </>
              ) : <p className="text-muted-foreground">No usage recorded for this range.</p>}
              {pricing ? (
                <div className={cn(card, "flex flex-col gap-3")}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Model pricing</div>
                    <Button variant="secondary" className="py-1.5 text-sm" data-testid="sync-pricing" onClick={syncPricing}>Sync from OpenAI reference</Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">Text: $ / 1M tokens (input, output). whisper-1: $ / minute. tts-1: $ / 1M characters. web_search: $ / call.</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] text-muted-foreground" data-testid="pricing-reviewed">Prices last reviewed: <span className="font-semibold">{pricingReviewedAt ? fmtDate(pricingReviewedAt) : "never"}</span></span>
                    <Button variant="ghost" className="py-1 text-xs" data-testid="pricing-mark-reviewed" onClick={markPricingReviewed}>Mark reviewed today</Button>
                  </div>
                  {(() => { const days = pricingReviewedAt ? Math.floor((Date.now() - new Date(pricingReviewedAt).getTime()) / 86400000) : null; return (days === null || days > 90) ? (<p className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-2 text-[11px] text-amber-700" data-testid="pricing-stale">It's been {days === null ? "a while" : `${days} days`} — re-check OpenAI's current rates, then hit "Save pricing" or "Mark reviewed today" to reset this reminder.</p>) : null; })()}
                  {Object.keys(pricing).map((m) => (
                    <div key={m} className="flex flex-wrap items-center gap-2">
                      <span className="w-40 truncate text-sm font-semibold">{m}</span>
                      {Object.keys(pricing[m]).map((field) => (
                        <label key={field} className="flex items-center gap-1 text-xs text-muted-foreground">{field}
                          <input data-testid={`price-${m}-${field}`} type="number" step="0.001" value={pricing[m][field]}
                            onChange={(e) => setPricing((p) => ({ ...p, [m]: { ...p[m], [field]: e.target.value } }))}
                            className="w-24 rounded-lg border border-input bg-background px-2 py-1.5 text-sm outline-none" />
                        </label>
                      ))}
                    </div>
                  ))}
                  <Button data-testid="save-pricing" variant="secondary" className="py-2 text-sm" onClick={savePricing}>Save pricing</Button>
                </div>
              ) : null}
              <div className={cn(card, "flex flex-col gap-2 sm:flex-row sm:items-end")}>
                <div className="flex-1">
                  <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Monthly cost alert</div>
                  <label className="text-xs text-muted-foreground">Budget in USD (0 = off)</label>
                  <input data-testid="budget-input" type="number" min="0" step="1" value={budgetInput} onChange={(e) => setBudgetInput(e.target.value)} className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none" />
                </div>
                <Button data-testid="save-budget" variant="secondary" className="py-2 text-sm" onClick={saveBudget}>Save budget</Button>
              </div>
            </div>
            )
          ) : null}

          {section === "Reports" ? (
            <div className="flex flex-col gap-3">
              {socialReports.length === 0 ? <p className="text-muted-foreground">No reports.</p> : socialReports.map((r) => {
                const mod = r.offender_moderation;
                const sug = mod && mod.suggestion;
                const isSug = (a, d) => !!sug && sug.action === a && (d === undefined ? sug.days === undefined : sug.days === d);
                const sugTag = (a, d) => (isSug(a, d) ? " · suggested" : "");
                return (
                <div key={r.id} data-testid={`report-${r.id}`} className={cn(card, "flex flex-col gap-2")}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold capitalize">{r.target_type} report · {r.reason || "no reason"}</div>
                      <div className="truncate text-sm text-muted-foreground">
                        by {r.reporter_name || "user"}
                        {r.reported_message ? ` · "${r.reported_message.text}" — ${r.reported_message.sender_name}` : ""}
                        {r.reported_user ? ` · ${r.reported_user.name}` : ""}
                      </div>
                      {mod ? (
                        <div data-testid={`offender-status-${r.id}`} className="mt-1 text-xs font-semibold">
                          {mod.name}: <span className={mod.status === "active" ? "text-muted-foreground" : "text-primary"}>
                            {mod.status === "banned" ? "Banned from group chats" : mod.status === "suspended" ? `Suspended until ${fmtDate(mod.suspended_until)}` : "Active"}</span>
                          {mod.warnings ? ` · ${mod.warnings} warning${mod.warnings === 1 ? "" : "s"}` : ""}
                        </div>
                      ) : null}
                      {sug ? (
                        <div data-testid={`suggestion-${r.id}`} className="mt-1 inline-flex w-fit items-center rounded-full bg-secondary px-2.5 py-0.5 text-xs font-semibold text-primary">Suggested: {sug.label} — {sug.reason}</div>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {r.status === "open" ? (
                        <Button data-testid={`resolve-report-${r.id}`} variant="secondary" className="py-2 text-sm" onClick={() => resolveReport(r.id)}>Mark reviewed</Button>
                      ) : <Check className="h-5 w-5" />}
                      {r.offender_id ? (
                        <div className="relative">
                          <button data-testid={`mod-menu-${r.id}`} className="rounded-lg border border-border px-3 py-1.5 text-lg leading-none" onClick={() => setModMenu(modMenu === r.id ? null : r.id)}>⋯</button>
                          {modMenu === r.id ? (
                            <div className="absolute right-0 z-20 mt-1 flex w-60 flex-col overflow-hidden rounded-xl border border-border bg-popover shadow-lg">
                              <button data-testid={`mod-history-${r.id}`} className="border-b border-border px-4 py-2.5 text-left text-sm hover:bg-secondary" onClick={() => { setModMenu(null); viewHistory(r.offender_id); }}>View history</button>
                              <button data-testid={`mod-warn-${r.id}`} className={cn("px-4 py-2.5 text-left text-sm hover:bg-secondary", isSug("warn") && "font-bold")} onClick={() => moderate(r.offender_id, "warn")}>Warn user{sugTag("warn")}</button>
                              <button data-testid={`mod-suspend-3-${r.id}`} className={cn("px-4 py-2.5 text-left text-sm hover:bg-secondary", isSug("suspend", 3) && "font-bold")} onClick={() => moderate(r.offender_id, "suspend", 3)}>Suspend 3 days{sugTag("suspend", 3)}</button>
                              <button data-testid={`mod-suspend-7-${r.id}`} className={cn("px-4 py-2.5 text-left text-sm hover:bg-secondary", isSug("suspend", 7) && "font-bold")} onClick={() => moderate(r.offender_id, "suspend", 7)}>Suspend 7 days{sugTag("suspend", 7)}</button>
                              <button data-testid={`mod-suspend-30-${r.id}`} className={cn("px-4 py-2.5 text-left text-sm hover:bg-secondary", isSug("suspend", 30) && "font-bold")} onClick={() => moderate(r.offender_id, "suspend", 30)}>Suspend 1 month{sugTag("suspend", 30)}</button>
                              <button data-testid={`mod-ban-${r.id}`} className={cn("px-4 py-2.5 text-left text-sm text-primary hover:bg-secondary", isSug("ban") && "font-bold")} onClick={() => moderate(r.offender_id, "ban")}>Ban from group chats{sugTag("ban")}</button>
                              <button data-testid={`mod-reinstate-${r.id}`} className="border-t border-border px-4 py-2.5 text-left text-sm hover:bg-secondary" onClick={() => moderate(r.offender_id, "reinstate")}>Reinstate</button>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          ) : null}

          {section === "Prompts" && prompts ? (
            <div className="flex flex-col gap-4">
              {PTABS.map((t) => (
                <div key={t} className={cn(card, "flex flex-col gap-2")}>
                  <div className="font-bold capitalize">{t}</div>
                  {[0, 1, 2].map((i) => (
                    <input key={i} data-testid={`prompt-${t}-${i}`} value={prompts[t]?.[i] || ""} onChange={(e) => setPrompts((p) => { const c = { ...p, [t]: [...(p[t] || ["", "", ""])] }; c[t][i] = e.target.value; return c; })}
                      className="rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none" />
                  ))}
                </div>
              ))}
              <Button data-testid="save-prompts" onClick={savePrompts}>Save starter prompts</Button>
            </div>
          ) : null}
        </>
      )}

      {modHistory ? (
        <div data-testid="mod-history-modal" className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-6" onClick={() => setModHistory(null)}>
          <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border border-border bg-background p-6 sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-lg font-bold">{modHistory.name}</div>
                <div className="text-sm text-muted-foreground">{modHistory.warnings} warning{modHistory.warnings === 1 ? "" : "s"} · {modHistory.suspensions} suspension{modHistory.suspensions === 1 ? "" : "s"} · {modHistory.status}</div>
              </div>
              <button data-testid="mod-history-close" onClick={() => setModHistory(null)} className="p-1 text-muted-foreground"><X className="h-5 w-5" /></button>
            </div>
            {modHistory.suggestion ? (
              <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-secondary p-3">
                <span className="text-sm">Suggested next step: <span className="font-semibold text-primary">{modHistory.suggestion.label}</span> <span className="text-muted-foreground">({modHistory.suggestion.reason})</span></span>
                <Button data-testid="mod-history-apply" variant="secondary" className="ml-auto py-1.5 text-sm" onClick={() => moderate(modHistory.user_id, modHistory.suggestion.action, modHistory.suggestion.days)}>Apply</Button>
              </div>
            ) : null}
            <div className="mt-4 flex flex-col gap-2">
              {modHistory.history.length === 0 ? <p className="text-sm text-muted-foreground">No moderation actions yet.</p> : modHistory.history.map((h, i) => (
                <div key={i} data-testid={`mod-history-row-${i}`} className="flex flex-col gap-0.5 rounded-xl border border-border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold capitalize">{h.action === "suspend" ? `Suspended ${h.days} days` : h.action === "reinstate" ? "Reinstated" : h.action === "ban" ? "Banned" : "Warned"}</span>
                    <span className="text-xs text-muted-foreground">{fmtDate(h.at)}</span>
                  </div>
                  {h.reason ? <div className="text-sm text-muted-foreground">"{h.reason}"</div> : null}
                  <div className="text-xs text-muted-foreground">by {h.by_name}{h.until ? ` · until ${fmtDate(h.until)}` : ""}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </Shell>
  );
}

function UsageTable({ title, rows, card }) {
  const keys = Object.keys(rows || {});
  return (
    <div className={card}>
      <div className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</div>
      {keys.length === 0 ? <p className="text-sm text-muted-foreground">No data.</p> : (
        <div className="flex flex-col gap-1.5">
          <div className="flex text-xs font-semibold text-muted-foreground"><span className="flex-1">Name</span><span className="w-16 text-right">Calls</span><span className="w-28 text-right">Tokens</span><span className="w-24 text-right">Cost</span></div>
          {keys.map((k) => (
            <div key={k} data-testid={`usage-row-${k}`} className="flex text-sm">
              <span className="flex-1 truncate">{k}</span>
              <span className="w-16 text-right">{rows[k].calls}</span>
              <span className="w-28 text-right">{rows[k].total_tokens.toLocaleString()}</span>
              <span className="w-24 text-right">${rows[k].cost.toFixed(4)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TopUsersTable({ users, card, onSelect, onViewAll }) {
  const rows = users || [];
  return (
    <div className={card}>
      <div className="mb-2 flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Top users by cost</div>
        {onViewAll ? <button data-testid="usage-view-all-users" onClick={onViewAll} className="text-xs font-semibold text-foreground underline">View all users →</button> : null}
      </div>
      {rows.length === 0 ? <p className="text-sm text-muted-foreground">No user activity in this range.</p> : (
        <div className="flex flex-col gap-1.5">
          <div className="flex text-xs font-semibold text-muted-foreground"><span className="w-6">#</span><span className="flex-1">User</span><span className="w-16 text-right">Calls</span><span className="w-28 text-right">Tokens</span><span className="w-24 text-right">Cost</span></div>
          {rows.map((u, i) => (
            <button key={u.user_id} data-testid={`usage-user-${i}`} onClick={() => onSelect && onSelect(u.user_id)}
              className="flex items-center rounded-lg px-1 py-1 text-left text-sm hover:bg-secondary">
              <span className="w-6 text-muted-foreground">{i + 1}</span>
              <span className="flex-1 truncate">{u.name}</span>
              <span className="w-16 text-right">{u.calls}</span>
              <span className="w-28 text-right">{u.total_tokens.toLocaleString()}</span>
              <span className="w-24 text-right font-semibold">${u.cost.toFixed(4)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function UsageBreakdowns({ qs, card }) {
  const [schools, setSchools] = useState(null);
  const [affiliates, setAffiliates] = useState(null);
  const [schoolDetail, setSchoolDetail] = useState(null);
  const [affiliateDetail, setAffiliateDetail] = useState(null);
  useEffect(() => {
    let alive = true;
    setSchoolDetail(null); setAffiliateDetail(null); setSchools(null); setAffiliates(null);
    (async () => {
      try {
        const [sc, af] = await Promise.all([
          apiFetch(`/developer/reports/usage/by-school?${qs}`),
          apiFetch(`/developer/reports/usage/by-affiliate?${qs}`),
        ]);
        if (alive) { setSchools(sc); setAffiliates(af); }
      } catch (e) { toast(e.message); }
    })();
    return () => { alive = false; };
  }, [qs]);
  const openSchool = async (id) => { try { setSchoolDetail(await apiFetch(`/developer/reports/usage/schools/${id}?${qs}`)); } catch (e) { toast(e.message); } };
  const openAffiliate = async (id) => { try { setAffiliateDetail(await apiFetch(`/developer/reports/usage/affiliates/${id}?${qs}`)); } catch (e) { toast(e.message); } };
  const money = (v) => `$${(v || 0).toFixed(4)}`;
  const dlCsv = (path, name) => downloadFile(path, name).catch((e) => toast(e.message));

  if (schoolDetail) {
    return (
      <div className={cn(card, "flex flex-col gap-2")} data-testid="usage-school-detail">
        <div className="flex items-center justify-between">
          <div><span className="font-bold">{schoolDetail.name}</span> <span className="font-mono text-xs text-muted-foreground">{schoolDetail.school_code}</span></div>
          <button onClick={() => setSchoolDetail(null)} className="text-xs font-semibold underline" data-testid="usage-school-back">← Back</button>
        </div>
        <div className="text-sm text-muted-foreground">Total <span className="font-semibold text-foreground">{money(schoolDetail.totals.cost)}</span> · {schoolDetail.totals.calls} calls · {schoolDetail.totals.total_tokens.toLocaleString()} tokens</div>
        <div className="mt-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">By teacher</div>
        {schoolDetail.teachers.length === 0 ? <p className="text-sm text-muted-foreground">No teachers.</p> : schoolDetail.teachers.map((t) => (
          <div key={t.teacher_id || "unassigned"} data-testid={`usage-teacher-${t.teacher_id || "unassigned"}`} className="flex text-sm">
            <span className="flex-1 truncate">{t.name} <span className="text-muted-foreground">({t.students} student{t.students === 1 ? "" : "s"})</span></span>
            <span className="w-16 text-right">{t.calls}</span>
            <span className="w-28 text-right">{t.total_tokens.toLocaleString()}</span>
            <span className="w-24 text-right font-semibold">{money(t.cost)}</span>
          </div>
        ))}
        <div className="mt-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">By student</div>
        {schoolDetail.students.length === 0 ? <p className="text-sm text-muted-foreground">No students.</p> : schoolDetail.students.map((s) => (
          <div key={s.user_id} data-testid={`usage-student-${s.user_id}`} className="flex text-sm">
            <span className="flex-1 truncate">{s.name}{s.teacher_name ? <span className="text-muted-foreground"> · {s.teacher_name}</span> : null}</span>
            <span className="w-16 text-right">{s.calls}</span>
            <span className="w-28 text-right">{s.total_tokens.toLocaleString()}</span>
            <span className="w-24 text-right font-semibold">{money(s.cost)}</span>
          </div>
        ))}
      </div>
    );
  }
  if (affiliateDetail) {
    return (
      <div className={cn(card, "flex flex-col gap-2")} data-testid="usage-affiliate-detail">
        <div className="flex items-center justify-between">
          <div><span className="font-bold">{affiliateDetail.name}</span> <span className="font-mono text-xs text-muted-foreground">{affiliateDetail.coupon_code}</span></div>
          <button onClick={() => setAffiliateDetail(null)} className="text-xs font-semibold underline" data-testid="usage-affiliate-back">← Back</button>
        </div>
        <div className="text-sm text-muted-foreground">Total <span className="font-semibold text-foreground">{money(affiliateDetail.totals.cost)}</span> · {affiliateDetail.totals.calls} calls · {affiliateDetail.referred_users.length} referred user(s)</div>
        <div className="mt-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">Referred users</div>
        {affiliateDetail.referred_users.length === 0 ? <p className="text-sm text-muted-foreground">No referred-user activity in this range.</p> : affiliateDetail.referred_users.map((u) => (
          <div key={u.user_id} data-testid={`usage-ref-user-${u.user_id}`} className="flex text-sm">
            <span className="flex-1 truncate">{u.name}</span>
            <span className="w-16 text-right">{u.calls}</span>
            <span className="w-28 text-right">{u.total_tokens.toLocaleString()}</span>
            <span className="w-24 text-right font-semibold">{money(u.cost)}</span>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <div className={cn(card, "flex flex-col gap-2")} data-testid="usage-by-school-card">
        <div className="flex items-center justify-between">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Usage by school</div>
          <button onClick={() => dlCsv(`/developer/reports/usage/by-school.csv?${qs}`, "usage_by_school.csv")} className="text-xs underline" data-testid="usage-by-school-csv">CSV</button>
        </div>
        {!schools ? <p className="text-sm text-muted-foreground">Loading…</p> : schools.schools.length === 0 ? <p className="text-sm text-muted-foreground">No school usage in this range.</p> : (
          <div className="flex flex-col gap-1">
            <div className="flex text-xs font-semibold text-muted-foreground"><span className="flex-1">School</span><span className="w-20 text-right">Students</span><span className="w-24 text-right">Cost</span></div>
            {schools.schools.map((s) => (
              <button key={s.school_id} data-testid={`usage-school-${s.school_id}`} onClick={() => openSchool(s.school_id)} className="flex items-center rounded-lg px-1 py-1 text-left text-sm hover:bg-secondary">
                <span className="flex-1 truncate">{s.name}</span>
                <span className="w-20 text-right">{s.active_students}</span>
                <span className="w-24 text-right font-semibold">{money(s.cost)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className={cn(card, "flex flex-col gap-2")} data-testid="usage-by-affiliate-card">
        <div className="flex items-center justify-between">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Usage by affiliate</div>
          <button onClick={() => dlCsv(`/developer/reports/usage/by-affiliate.csv?${qs}`, "usage_by_affiliate.csv")} className="text-xs underline" data-testid="usage-by-affiliate-csv">CSV</button>
        </div>
        {!affiliates ? <p className="text-sm text-muted-foreground">Loading…</p> : affiliates.affiliates.length === 0 ? <p className="text-sm text-muted-foreground">No affiliate-referred usage in this range.</p> : (
          <div className="flex flex-col gap-1">
            <div className="flex text-xs font-semibold text-muted-foreground"><span className="flex-1">Affiliate</span><span className="w-20 text-right">Referred</span><span className="w-24 text-right">Cost</span></div>
            {affiliates.affiliates.map((a) => (
              <button key={a.affiliate_id} data-testid={`usage-affiliate-${a.affiliate_id}`} onClick={() => openAffiliate(a.affiliate_id)} className="flex items-center rounded-lg px-1 py-1 text-left text-sm hover:bg-secondary">
                <span className="flex-1 truncate">{a.name}</span>
                <span className="w-20 text-right">{a.active_referred}</span>
                <span className="w-24 text-right font-semibold">{money(a.cost)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function UserDetailPanel({ detail, card, onBack, onExport, onHistory }) {
  const t = detail.totals || {};
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <button data-testid="user-detail-back" onClick={onBack} className="flex items-center gap-1 text-sm font-semibold text-muted-foreground"><ChevronLeft className="h-4 w-4" /> Back</button>
        <div className="flex items-center gap-2">
          {onHistory ? <Button data-testid="user-detail-history" variant="secondary" className="py-1.5 text-sm" onClick={onHistory}>Moderation history</Button> : null}
          <Button data-testid="export-statement-csv" variant="secondary" className="py-1.5 text-sm" onClick={onExport}><Download className="h-4 w-4" /> Statement</Button>
        </div>
      </div>
      <div className={card}>
        <div className="text-lg font-bold" data-testid="user-detail-name">{detail.name}</div>
        {detail.email ? <div className="text-sm text-muted-foreground">{detail.email}</div> : null}
        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
          <div><div className="text-xs uppercase text-muted-foreground">Cost</div><div className="text-xl font-bold">${(t.cost || 0).toFixed(4)}</div></div>
          <div><div className="text-xs uppercase text-muted-foreground">Calls</div><div className="text-xl font-bold">{t.calls || 0}</div></div>
          <div><div className="text-xs uppercase text-muted-foreground">Tokens</div><div className="text-xl font-bold">{(t.total_tokens || 0).toLocaleString()}</div></div>
          <div><div className="text-xs uppercase text-muted-foreground">In / Out</div><div className="text-sm font-semibold">{(t.input_tokens || 0).toLocaleString()} / {(t.output_tokens || 0).toLocaleString()}</div></div>
        </div>
      </div>
      <UsageTable title="By model" rows={detail.by_model} card={card} />
      <UsageTable title="By tab / feature" rows={detail.by_tab} card={card} />
    </div>
  );
}


function AllUsersPanel({ data, card, search, setSearch, sort, onSort, onSearch, onSelect, onExport, onBack }) {
  const users = (data && data.users) || [];
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <button data-testid="all-users-back" onClick={onBack} className="flex items-center gap-1 text-sm font-semibold text-muted-foreground"><ChevronLeft className="h-4 w-4" /> Back</button>
        <Button data-testid="export-users-csv" variant="secondary" className="py-1.5 text-sm" onClick={onExport}><Download className="h-4 w-4" /> All users CSV</Button>
      </div>
      <div className="flex gap-3">
        <div className={cn(card, "flex-1")}><div className="text-xs uppercase text-muted-foreground">Users</div><div className="text-2xl font-bold" data-testid="all-users-count">{data ? data.count : 0}</div></div>
        <div className={cn(card, "flex-1")}><div className="text-xs uppercase text-muted-foreground">Avg / user</div><div className="text-2xl font-bold">${((data && data.avg_cost_per_user) || 0).toFixed(4)}</div></div>
        <div className={cn(card, "flex-1")}><div className="text-xs uppercase text-muted-foreground">Total cost</div><div className="text-2xl font-bold">${((data && data.total_cost) || 0).toFixed(4)}</div></div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input data-testid="all-users-search" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onSearch()} placeholder="Search name or email…" className="min-w-[180px] flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none" />
        <Button data-testid="all-users-search-btn" variant="secondary" className="py-2 text-sm" onClick={onSearch}>Search</Button>
        {[["cost", "Cost"], ["total_tokens", "Tokens"], ["calls", "Calls"]].map(([k, l]) => (
          <button key={k} data-testid={`all-users-sort-${k}`} onClick={() => onSort(k)} className={cn("rounded-full border px-3 py-1.5 text-xs font-semibold", sort === k ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground")}>{l}</button>
        ))}
      </div>
      {users.length === 0 ? <p className="text-muted-foreground">No users match.</p> : (
        <div className="flex flex-col gap-1">
          <div className="flex text-xs font-semibold text-muted-foreground"><span className="flex-1">User</span><span className="w-16 text-right">Calls</span><span className="w-28 text-right">Tokens</span><span className="w-24 text-right">Cost</span></div>
          {users.map((u, i) => (
            <button key={u.user_id} data-testid={`all-user-${i}`} onClick={() => onSelect(u.user_id)} className="flex items-center rounded-lg px-1 py-2 text-left text-sm hover:bg-secondary">
              <span className="min-w-0 flex-1"><span className="block truncate font-medium">{u.name}</span>{u.email ? <span className="block truncate text-xs text-muted-foreground">{u.email}</span> : null}</span>
              <span className="w-16 text-right">{u.calls}</span>
              <span className="w-28 text-right">{u.total_tokens.toLocaleString()}</span>
              <span className="w-24 text-right font-semibold">${u.cost.toFixed(4)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function CostTrendChart({ daily, card }) {
  const data = daily || [];
  if (data.length === 0) return null;
  const W = 640, H = 140, P = 28;
  const max = Math.max(...data.map((d) => d.cost), 0.000001);
  const n = data.length;
  const x = (i) => (n === 1 ? W / 2 : P + (i * (W - 2 * P)) / (n - 1));
  const y = (c) => H - P - (c / max) * (H - 2 * P);
  const line = data.map((d, i) => `${x(i)},${y(d.cost)}`).join(" ");
  const area = `${x(0)},${H - P} ${line} ${x(n - 1)},${H - P}`;
  return (
    <div className={card}>
      <div className="mb-3 flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Daily modeled cost</div>
        <div className="text-xs text-muted-foreground">peak ${max.toFixed(4)}</div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" data-testid="usage-trend-chart">
        <polygon points={area} className="fill-foreground/10" />
        <polyline points={line} className="fill-none stroke-foreground" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {data.map((d, i) => <circle key={d.date} cx={x(i)} cy={y(d.cost)} r="3" className="fill-background stroke-foreground" strokeWidth="2" />)}
      </svg>
      <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
        <span>{data[0].date}</span>
        <span>{data[n - 1].date}</span>
      </div>
    </div>
  );
}


